import {
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import {
  newPointId,
  newTrace,
  nextPointSequence,
  replaceTrace,
  updateTrace,
  defaultChartRegion,
} from './editorState';
import { pixelPointToSource, sourcePointToPixel } from './geometry';

import { readableCurveError } from './errors';
import type { CurveImportApi } from './api';
import type {
  ChartRegion,
  CurveDraftDocument,
  CurveKind,
  CurveTraceDraft,
  PixelPoint,
  SourceFormat,
  TracePointDraft,
} from './types';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { curveImportFieldHelp } from '../../app/guidance/fieldHelpRegistry';

interface CurveDigitizerProps {
  activeTraceId: string | null;
  api: CurveImportApi;
  document: CurveDraftDocument;
  importId: string;
  onActiveTraceChange: (id: string | null) => void;
  onBusyChange?: (busy: boolean) => void;
  onCommit: (document: CurveDraftDocument) => void;
  onError: (message: string) => void;
  pageCount: number | null;
  sourceFormat: SourceFormat;
  suggestedSpeeds: number[];
}

const curveKinds: CurveKind[] = [
  'head',
  'efficiency',
  'efficiency_contour',
  'brake_power',
  'brake_power_contour',
  'npshr',
  'npshr_contour',
  'suction_lift',
  'operating_boundary',
];

const imageSuggestionKinds = new Set<CurveKind>([
  'head',
  'efficiency',
  'brake_power',
  'npshr',
  'suction_lift',
]);

function finitePixel(point: PixelPoint): PixelPoint | null {
  return Number.isFinite(point.x) && Number.isFinite(point.y) ? point : null;
}

function displayPoint(
  trace: CurveTraceDraft,
  point: TracePointDraft,
  document: CurveDraftDocument,
  imageSize: { height: number; width: number },
): PixelPoint | null {
  if (point.pixel !== null) return point.pixel;
  if (point.page_x_normalized != null && point.page_y_normalized != null) {
    return finitePixel({
      x: point.page_x_normalized * imageSize.width,
      y: point.page_y_normalized * imageSize.height,
    });
  }
  const calibration = trace.calibration_override ?? document.calibration;
  if (calibration === null || point.source === null) return null;
  return finitePixel(sourcePointToPixel(calibration, point.source));
}

function selectedRegion(
  document: CurveDraftDocument,
  width: number,
  height: number,
): ChartRegion {
  return (
    document.selected_chart ??
    defaultChartRegion(document.selected_page ?? 1, width, height)
  );
}

export function CurveDigitizer({
  activeTraceId,
  api,
  document,
  importId,
  onActiveTraceChange,
  onBusyChange,
  onCommit,
  onError,
  pageCount,
  sourceFormat,
  suggestedSpeeds,
}: CurveDigitizerProps) {
  const [imageSize, setImageSize] = useState({ height: 750, width: 1000 });
  const [newKind, setNewKind] = useState<CurveKind>('head');
  const [newLabel, setNewLabel] = useState('Head curve');
  const [newSpeed, setNewSpeed] = useState(
    suggestedSpeeds[0] === undefined ? '' : String(suggestedSpeeds[0]),
  );
  const [resampleCount, setResampleCount] = useState('20');
  const [suggesting, setSuggesting] = useState(false);
  const [resampling, setResampling] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [interactionMode, setInteractionMode] = useState<
    'add_points' | 'inspect'
  >('inspect');
  const [cursorPixel, setCursorPixel] = useState<PixelPoint | null>(null);
  const [lastInspectedPixel, setLastInspectedPixel] =
    useState<PixelPoint | null>(null);
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const activeTrace =
    document.traces.find((trace) => trace.id === activeTraceId) ?? null;
  const page = document.selected_page ?? 1;
  const activeTraceMatchesPage =
    activeTrace !== null &&
    (activeTrace.page_number === null || activeTrace.page_number === page) &&
    (activeTrace.chart_region === null ||
      activeTrace.chart_region.page_number === page);
  const region = selectedRegion(document, imageSize.width, imageSize.height);
  const pageUrl = api.pageUrl(importId, page);

  const commitRegion = (next: ChartRegion) => {
    onCommit({
      ...document,
      selected_chart: next,
      selected_page: next.page_number,
    });
  };

  const addTrace = () => {
    const trace = newTrace(
      newKind,
      newLabel.trim() || 'Measured curve',
      page,
      region,
      newSpeed.trim() === '' ? null : Number(newSpeed),
    );
    onCommit({ ...document, traces: [...document.traces, trace] });
    onActiveTraceChange(trace.id);
    setInteractionMode('add_points');
    setSelectedPointId(trace.points[0]?.id ?? null);
  };

  const removeTrace = () => {
    if (activeTrace === null) return;
    const traces = document.traces.filter((trace) => trace.id !== activeTrace.id);
    onCommit({ ...document, traces });
    onActiveTraceChange(traces[0]?.id ?? null);
    setSelectedPointId(traces[0]?.points[0]?.id ?? null);
    if (traces.length === 0) setInteractionMode('inspect');
  };

  const addPointAtPixel = (pixel: PixelPoint) => {
    if (activeTrace === null) {
      onError('Select or add a trace before adding points.');
      return;
    }
    const usesSourceCoordinates = activeTrace.points[0]?.source !== null;
    const calibration = activeTrace.calibration_override ?? document.calibration;
    if (usesSourceCoordinates && calibration === null) {
      onError('Calibrate this chart before adding to a manual-coordinate trace.');
      return;
    }
    const point: TracePointDraft = {
      id: newPointId(),
      pixel: usesSourceCoordinates ? null : pixel,
      sequence: nextPointSequence(activeTrace),
      source:
        usesSourceCoordinates && calibration !== null
          ? pixelPointToSource(calibration, pixel)
          : null,
    };
    onCommit(
      updateTrace(document, activeTrace.id, (trace) => ({
        ...trace,
        points: [...trace.points, point],
      })),
    );
    setSelectedPointId(point.id);
  };

  const overlayPixel = (
    element: SVGSVGElement,
    clientX: number,
    clientY: number,
  ): PixelPoint => {
    const bounds = element.getBoundingClientRect();
    const x =
      ((clientX - bounds.left) / Math.max(bounds.width, 1)) *
      imageSize.width;
    const y =
      ((clientY - bounds.top) / Math.max(bounds.height, 1)) *
      imageSize.height;
    return {
      x: Math.min(imageSize.width, Math.max(0, x)),
      y: Math.min(imageSize.height, Math.max(0, y)),
    };
  };

  const inspectOrAddPoint = (pixel: PixelPoint) => {
    setLastInspectedPixel(pixel);
    if (interactionMode === 'add_points') addPointAtPixel(pixel);
  };

  const overlayClick = (event: MouseEvent<SVGSVGElement>) => {
    if (event.target !== event.currentTarget) return;
    inspectOrAddPoint(
      overlayPixel(event.currentTarget, event.clientX, event.clientY),
    );
  };

  const overlayKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    if (
      event.target !== event.currentTarget ||
      (event.key !== 'Enter' && event.key !== ' ')
    ) {
      return;
    }
    event.preventDefault();
    inspectOrAddPoint({
      x: (region.left_px + region.right_px) / 2,
      y: (region.top_px + region.bottom_px) / 2,
    });
  };

  const updatePointCoordinate = (
    pointId: string,
    coordinate: 'x' | 'y',
    value: number,
  ) => {
    if (activeTrace === null) return;
    setSelectedPointId(pointId);
    onCommit(
      updateTrace(document, activeTrace.id, (trace) => ({
        ...trace,
        points: trace.points.map((point) => {
          if (point.id !== pointId) return point;
          if (point.pixel !== null) {
            return {
              ...point,
              pixel: { ...point.pixel, [coordinate]: value },
            };
          }
          if (point.source !== null) {
            return {
              ...point,
              source: { ...point.source, [coordinate]: value },
            };
          }
          return point;
        }),
      })),
    );
  };

  const deletePoint = (pointId: string) => {
    if (activeTrace === null || activeTrace.points.length <= 2) return;
    onCommit(
      updateTrace(document, activeTrace.id, (trace) => ({
        ...trace,
        points: trace.points.filter((point) => point.id !== pointId),
      })),
    );
    if (selectedPointId === pointId) {
      setSelectedPointId(
        activeTrace.points.find((point) => point.id !== pointId)?.id ?? null,
      );
    }
  };

  const suggestTrace = async () => {
    if (!imageSuggestionKinds.has(newKind)) {
      onError('Contour and boundary traces require reviewer-entered points and labels.');
      return;
    }
    setSuggesting(true);
    onBusyChange?.(true);
    try {
      const trace = await api.suggestTrace(importId, {
        chart_region: region,
        kind: newKind,
        label: newLabel.trim() || 'Suggested curve',
        speed_rpm: newSpeed.trim() === '' ? null : Number(newSpeed),
      });
      onCommit({ ...document, traces: [...document.traces, trace] });
      onActiveTraceChange(trace.id);
      setSelectedPointId(trace.points[0]?.id ?? null);
      setInteractionMode('inspect');
    } catch (error: unknown) {
      onError(readableCurveError(error, 'Trace suggestion failed.'));
    } finally {
      setSuggesting(false);
      onBusyChange?.(false);
    }
  };

  const resample = async () => {
    if (activeTrace === null) return;
    setResampling(true);
    onBusyChange?.(true);
    try {
      const replacement = await api.resampleTrace(
        activeTrace,
        Number(resampleCount),
      );
      onCommit(replaceTrace(document, activeTrace.id, replacement));
    } catch (error: unknown) {
      onError(readableCurveError(error, 'Trace resampling failed.'));
    } finally {
      setResampling(false);
      onBusyChange?.(false);
    }
  };

  const updateTraceKind = (kind: CurveKind) => {
    if (activeTrace === null) return;
    onCommit(
      updateTrace(document, activeTrace.id, (trace) => ({
        ...trace,
        contour_unit:
          kind === 'efficiency_contour'
            ? (trace.contour_unit ?? '%')
            : kind === 'brake_power_contour'
              ? (trace.contour_unit ?? 'hp')
              : null,
        contour_value:
          kind === 'efficiency_contour'
            ? (trace.contour_value ?? 75)
            : kind === 'brake_power_contour'
              ? (trace.contour_value ?? 1)
              : null,
        kind,
      })),
    );
  };

  const overlayTrace = activeTraceMatchesPage ? activeTrace : null;
  const overlayPoints =
    overlayTrace === null
      ? []
      : overlayTrace.points
          .slice()
          .sort((left, right) => left.sequence - right.sequence)
          .map((point) => ({
            pixel: displayPoint(overlayTrace, point, document, imageSize),
            point,
          }))
          .filter(
            (entry): entry is { pixel: PixelPoint; point: TracePointDraft } =>
              entry.pixel !== null,
          );

  return (
    <div className="curve-digitizer">
      <section
        aria-labelledby="curve-manual-workflow-title"
        className="curve-manual-workflow"
        data-testid="curve-document-editor"
      >
        <div>
          <p className="eyebrow">Guaranteed manual path</p>
          <h3 id="curve-manual-workflow-title">Manual digitizing workflow</h3>
        </div>
        <ol>
          <li><strong>Choose the page and chart box.</strong> Use the full-page button or enter the dashed box boundaries.</li>
          <li><strong>Inspect two printed ticks on each axis.</strong> Use Inspect pixels, note x/y coordinates, then enter those pixels, printed values, units, scale, and direction in Calibration.</li>
          <li><strong>Select or add a trace.</strong> Switch to Add points, then click along the visible curve. New traces start with two clearly editable placeholder points because a valid draft requires at least two.</li>
          <li><strong>Review, save, and validate.</strong> Select a point to find its coordinate row, correct values, then save before publication review.</li>
        </ol>
        <p className="curve-editor-state" aria-live="polite">
          <strong>Current editor state:</strong> Page {page};{' '}
          {document.selected_chart === null ? 'full-page chart default' : 'chart box selected'};{' '}
          {document.calibration === null ? 'calibration missing' : 'calibration ready'};{' '}
          {activeTrace === null
            ? 'no active trace'
            : `active trace “${activeTrace.label}” with ${activeTrace.points.length} points`};{' '}
          {interactionMode === 'inspect' ? 'Inspect pixels mode' : 'Add points mode'}
          {selectedPointId === null ? '' : '; one point selected'}.
        </p>
      </section>

      <section className="curve-source-panel" aria-labelledby="curve-source-title">
        <div className="curve-section-heading">
          <div>
            <p className="eyebrow">Step 1 · Source sheet</p>
            <h3 id="curve-source-title">Choose page and chart area</h3>
          </div>
          <label>
            Zoom
            <input {...guided(curveImportFieldHelp.overlayZoom)}
              aria-label="Overlay zoom"
              max="200"
              min="50"
              type="range"
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
            <span>{zoom}%</span>
          </label>
        </div>
        <div className="curve-page-controls">
          <label>
            Page
            <input {...guided(curveImportFieldHelp.chartPage)}
              aria-label="Selected chart page"
              max={pageCount ?? undefined}
              min="1"
              type="number"
              value={page}
              onChange={(event) => {
                const selectedPage = Number(event.target.value);
                onCommit({
                  ...document,
                  selected_chart: null,
                  selected_page: selectedPage,
                });
              }}
            />
          </label>
          <button
            type="button"
            onClick={() =>
              commitRegion(
                defaultChartRegion(page, imageSize.width, imageSize.height),
              )
            }
          >
            Use full page as chart
          </button>
          <span>
            {pageCount === null ? 'Page count pending' : `${pageCount} page source`}
          </span>
        </div>
        <p className="curve-help" id="curve-chart-region-instructions">
          The dashed rectangle is the saved chart area. Use{' '}
          <strong>Use full page as chart</strong>{' '}
          or enter its left, top, right, and bottom pixel boundaries below.
        </p>
        <div className="curve-region-fields">
          {(['left_px', 'top_px', 'right_px', 'bottom_px'] as const).map(
            (field) => (
              <label key={field}>
                {field.replace('_px', '').replace('_', ' ')} (px)
                <input {...guided(curveImportFieldHelp.chartBoundary, field)}
                  aria-label={`Chart ${field.replace('_px', '')}`}
                  min="0"
                  step="any"
                  type="number"
                  value={region[field]}
                  onChange={(event) =>
                    commitRegion({
                      ...region,
                      [field]: Number(event.target.value),
                      page_number: page,
                    })
                  }
                />
              </label>
            ),
          )}
        </div>
        {sourceFormat === 'csv' ? null : (
          <div className="curve-interaction-toolbar">
            <div aria-label="Document interaction mode" role="group">
              <button
                aria-pressed={interactionMode === 'inspect'}
                type="button"
                onClick={() => setInteractionMode('inspect')}
              >
                Inspect calibration pixels
              </button>
              <button
                aria-pressed={interactionMode === 'add_points'}
                disabled={activeTrace === null || !activeTraceMatchesPage}
                type="button"
                onClick={() => setInteractionMode('add_points')}
              >
                Add points to active trace
              </button>
            </div>
            <output aria-live="polite">
              {cursorPixel === null
                ? 'Move over the page to read source pixels.'
                : `Cursor x ${cursorPixel.x.toFixed(1)}, y ${cursorPixel.y.toFixed(1)}`}
              {lastInspectedPixel === null
                ? ''
                : ` · Last click x ${lastInspectedPixel.x.toFixed(1)}, y ${lastInspectedPixel.y.toFixed(1)}`}
            </output>
          </div>
        )}
        {sourceFormat === 'csv' ? (
          <div className="curve-manual-source">
            CSV points are shown through the active calibration. Edit their source
            coordinates in the trace panel.
          </div>
        ) : (
          <div className="curve-chart-viewport">
            <div
              className="curve-chart-canvas"
              style={{
                height: `${(imageSize.height * zoom) / 100}px`,
                width: `${(imageSize.width * zoom) / 100}px`,
              }}
            >
              <img
                alt={`Pump curve source page ${page}`}
                draggable="false"
                src={pageUrl}
                onLoad={(event) => {
                  const image = event.currentTarget;
                  if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                    setImageSize({
                      height: image.naturalHeight,
                      width: image.naturalWidth,
                    });
                  }
                }}
                onError={() =>
                  onError(
                    'The selected page image could not be rendered. Check the processing status or choose another page.',
                  )
                }
              />
              <svg
                aria-describedby="curve-overlay-instructions"
                aria-label="Digitization overlay"
                className="curve-chart-overlay"
                preserveAspectRatio="none"
                role="application"
                tabIndex={0}
                viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
                onClick={overlayClick}
                onKeyDown={overlayKeyDown}
                onMouseLeave={() => setCursorPixel(null)}
                onMouseMove={(event) =>
                  setCursorPixel(
                    overlayPixel(
                      event.currentTarget,
                      event.clientX,
                      event.clientY,
                    ),
                  )
                }
              >
                <rect
                  className="curve-chart-region"
                  height={region.bottom_px - region.top_px}
                  pointerEvents="none"
                  width={region.right_px - region.left_px}
                  x={region.left_px}
                  y={region.top_px}
                />
                {overlayPoints.length > 1 ? (
                  <polyline
                    className="curve-trace-line"
                    points={overlayPoints
                      .map(({ pixel }) => `${pixel.x},${pixel.y}`)
                      .join(' ')}
                    pointerEvents="none"
                  />
                ) : null}
                {overlayPoints.map(({ pixel, point }, index) => (
                  <circle
                    key={point.id}
                    aria-label={`Select point ${index + 1}`}
                    aria-pressed={selectedPointId === point.id}
                    className={`curve-trace-point${
                      selectedPointId === point.id ? ' is-selected' : ''
                    }`}
                    cx={pixel.x}
                    cy={pixel.y}
                    r="8"
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedPointId(point.id);
                      setLastInspectedPixel(pixel);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      event.stopPropagation();
                      setSelectedPointId(point.id);
                      setLastInspectedPixel(pixel);
                    }}
                  />
                ))}
              </svg>
            </div>
          </div>
        )}
        <p className="curve-help" id="curve-overlay-instructions">
          {interactionMode === 'inspect'
            ? 'Inspect mode: clicking records a pixel coordinate for axis calibration and does not change a trace.'
            : `Add points mode: each empty-chart click adds a point to ${activeTrace?.label ?? 'the active trace'}.`}{' '}
          Select an existing red point to highlight its editable row below. The
          overlay and image keep the same original-page pixel coordinates at every
          zoom level. Press Enter or Space on the overlay to inspect or add at the
          chart center when using a keyboard.
        </p>
        {activeTrace !== null && !activeTraceMatchesPage ? (
          <p className="curve-page-warning" role="status">
            {activeTrace.label} belongs to page {activeTrace.page_number ?? activeTrace.chart_region?.page_number};
            its overlay is hidden on page {page}.
          </p>
        ) : null}
      </section>

      <section className="curve-trace-panel" aria-labelledby="curve-trace-title">
        <div className="curve-section-heading">
          <div>
            <p className="eyebrow">Step 3 · Digitization</p>
            <h3 id="curve-trace-title">Curve traces</h3>
          </div>
          <span>{document.traces.length} traces</span>
        </div>
        <div className="curve-add-trace">
          <label>
            New trace label
            <input {...guided(curveImportFieldHelp.newTraceLabel)} value={newLabel} onChange={(event) => setNewLabel(event.target.value)} />
          </label>
          <label>
            Curve family
            <select {...guided(curveImportFieldHelp.curveFamily, 'new')}
              value={newKind}
              onChange={(event) => setNewKind(event.target.value as CurveKind)}
            >
              {curveKinds.map((kind) => (
                <option key={kind} value={kind}>
                  {kind.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <label>
            Speed (RPM)
            <input {...guided(curveImportFieldHelp.traceSpeed, 'new')}
              min="1"
              type="number"
              value={newSpeed}
              onChange={(event) => setNewSpeed(event.target.value)}
            />
          </label>
          <div className="curve-button-row">
            <button type="button" onClick={addTrace}>
              Add editable trace
            </button>
            <button
              disabled={suggesting || sourceFormat === 'csv'}
              type="button"
              onClick={() => void suggestTrace()}
            >
              {suggesting ? 'Suggesting…' : 'Experimental image trace (unverified)'}
            </button>
          </div>
          <p className="curve-image-suggestion-warning" role="note">
            Experimental local image tracing follows dark pixels and can latch
            onto a border, gridline, or unrelated line. It is not AI extraction
            and must never be treated as curve data until compared point by point
            with the source.
          </p>
        </div>
        {document.traces.length === 0 ? (
          <p className="curve-empty">No traces yet. Add a manual trace; its two starting points are placeholders you must move onto the visible curve.</p>
        ) : (
          <div className="curve-trace-layout">
            <div className="curve-trace-list" role="list" aria-label="Curve traces">
              {document.traces.map((trace) => (
                <div key={trace.id} role="listitem">
                  <button
                    aria-pressed={trace.id === activeTraceId}
                    className={trace.id === activeTraceId ? 'is-active' : ''}
                    type="button"
                    onClick={() => {
                      onActiveTraceChange(trace.id);
                      setSelectedPointId(trace.points[0]?.id ?? null);
                    }}
                  >
                    <strong>{trace.label}</strong>
                    <span>{trace.kind.replaceAll('_', ' ')}</span>
                    <small>
                      {trace.points.length} points
                      {trace.suggestion_confidence === null
                        ? ''
                        : ` · suggested ${Math.round(trace.suggestion_confidence * 100)}%`}
                    </small>
                  </button>
                </div>
              ))}
            </div>
            {activeTrace === null ? null : (
              <div className="curve-trace-editor">
                <div className="curve-form-grid">
                  <label>
                    Trace label
                    <input {...guided(curveImportFieldHelp.traceLabel, activeTrace.id)}
                      value={activeTrace.label}
                      onChange={(event) =>
                        onCommit(
                          updateTrace(document, activeTrace.id, (trace) => ({
                            ...trace,
                            label: event.target.value,
                          })),
                        )
                      }
                    />
                  </label>
                  <label>
                    Curve family
                    <select {...guided(curveImportFieldHelp.curveFamily, activeTrace.id)}
                      value={activeTrace.kind}
                      onChange={(event) =>
                        updateTraceKind(event.target.value as CurveKind)
                      }
                    >
                      {curveKinds.map((kind) => (
                        <option key={kind} value={kind}>
                          {kind.replaceAll('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Speed (RPM)
                    <input {...guided(curveImportFieldHelp.traceSpeed, activeTrace.id)}
                      min="1"
                      type="number"
                      value={activeTrace.speed_rpm ?? ''}
                      onChange={(event) =>
                        onCommit(
                          updateTrace(document, activeTrace.id, (trace) => ({
                            ...trace,
                            speed_rpm:
                              event.target.value === ''
                                ? null
                                : Number(event.target.value),
                          })),
                        )
                      }
                    />
                  </label>
                  <label>
                    Trim label
                    <input {...guided(curveImportFieldHelp.trimLabel, activeTrace.id)}
                      value={activeTrace.trim_label ?? ''}
                      onChange={(event) =>
                        onCommit(
                          updateTrace(document, activeTrace.id, (trace) => ({
                            ...trace,
                            trim_label: event.target.value || null,
                          })),
                        )
                      }
                    />
                  </label>
                  {activeTrace.kind === 'efficiency_contour' ||
                  activeTrace.kind === 'brake_power_contour' ? (
                    <>
                      <label>
                        Contour value
                        <input {...guided(curveImportFieldHelp.contourValue, activeTrace.id)}
                          step="any"
                          type="number"
                          value={activeTrace.contour_value ?? ''}
                          onChange={(event) =>
                            onCommit(
                              updateTrace(document, activeTrace.id, (trace) => ({
                                ...trace,
                                contour_value: Number(event.target.value),
                              })),
                            )
                          }
                        />
                      </label>
                      <label>
                        Contour unit
                        <input {...guided(curveImportFieldHelp.contourUnit, activeTrace.id)}
                          value={activeTrace.contour_unit ?? ''}
                          onChange={(event) =>
                            onCommit(
                              updateTrace(document, activeTrace.id, (trace) => ({
                                ...trace,
                                contour_unit: event.target.value,
                              })),
                            )
                          }
                        />
                      </label>
                    </>
                  ) : null}
                </div>
                <div className="curve-button-row">
                  <label>
                    Resample points
                    <input {...guided(curveImportFieldHelp.resampleCount, activeTrace.id)}
                      aria-label="Resample point count"
                      max="1000"
                      min="2"
                      type="number"
                      value={resampleCount}
                      onChange={(event) => setResampleCount(event.target.value)}
                    />
                  </label>
                  <button disabled={resampling} type="button" onClick={() => void resample()}>
                    {resampling ? 'Resampling…' : 'Resample trace'}
                  </button>
                  <button className="curve-danger" type="button" onClick={removeTrace}>
                    Remove trace
                  </button>
                </div>
                <ol className="curve-point-list">
                  {activeTrace.points.map((point, index) => {
                    const coordinate = point.pixel ?? point.source;
                    const coordinateName = point.pixel === null ? 'Source' : 'Pixel';
                    return (
                      <li
                        key={point.id}
                        aria-current={
                          selectedPointId === point.id ? 'true' : undefined
                        }
                      >
                        <fieldset
                          className={
                            selectedPointId === point.id ? 'is-selected' : ''
                          }
                        >
                          <legend>
                            Move point {index + 1}
                            {selectedPointId === point.id ? ' — selected' : ''}
                          </legend>
                          <label>
                            {coordinateName} x
                            <input {...guided(curveImportFieldHelp.pointX, point.id)}
                              aria-label={`Point ${index + 1} ${coordinateName.toLowerCase()} x`}
                              min={point.pixel === null ? undefined : 0}
                              step="any"
                              type="number"
                              value={coordinate?.x ?? ''}
                              onFocus={() => setSelectedPointId(point.id)}
                              onChange={(event) =>
                                updatePointCoordinate(
                                  point.id,
                                  'x',
                                  Number(event.target.value),
                                )
                              }
                            />
                          </label>
                          <label>
                            {coordinateName} y
                            <input {...guided(curveImportFieldHelp.pointY, point.id)}
                              aria-label={`Point ${index + 1} ${coordinateName.toLowerCase()} y`}
                              min={point.pixel === null ? undefined : 0}
                              step="any"
                              type="number"
                              value={coordinate?.y ?? ''}
                              onFocus={() => setSelectedPointId(point.id)}
                              onChange={(event) =>
                                updatePointCoordinate(
                                  point.id,
                                  'y',
                                  Number(event.target.value),
                                )
                              }
                            />
                          </label>
                          <button
                            disabled={activeTrace.points.length <= 2}
                            type="button"
                            onClick={() => deletePoint(point.id)}
                          >
                            Delete point {index + 1}
                          </button>
                        </fieldset>
                      </li>
                    );
                  })}
                </ol>
                <button
                  type="button"
                  onClick={() =>
                    addPointAtPixel({
                      x: (region.left_px + region.right_px) / 2,
                      y: (region.top_px + region.bottom_px) / 2,
                    })
                  }
                >
                  Add point at chart center
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
